import React, { useEffect, useState } from "react";
import "./faqs.scss";
import CommonHeader from "../../components/CommonHeader";
import faqIcon from "../../assets/images/faq-icon.svg";
import useFaqs from "../../Hooks/useFaqs";
import { useAppSelector } from "../../redux/hooks";
import { userGuides } from "../../redux/faqs/faqsSlice";
import Loading from "../../components/LoadingPage/Loading";
import { toast } from "react-toastify";

const ShareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5m-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3m11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3" />
  </svg>
);

const handleShareGuide = (e: React.MouseEvent, guideId: string) => {
  e.stopPropagation();
  const currentUrl = window.location.href.split('?')[0].split('#')[0].replace(/\/$/, '');
  const shareUrl = `${currentUrl}/${guideId}`;
  navigator.clipboard.writeText(shareUrl).then(() => {
    toast.success("Link copied to clipboard!");
  });
};

const RecursiveAccordion = ({
  guides,
  highlightText,
  depth = 0,
}: {
  guides: any[];
  highlightText: (text: string) => string;
  depth?: number;
}) => {
  if (!guides || guides.length === 0) return null;

  return (
    <div className={`accordion ${depth > 0 ? "nested-accordion" : ""}`}>
      {guides.map((guide: any) => {
        const hasChildren =
          guide?.childUserGuides && guide.childUserGuides.length > 0;

        return (
          <div className="accordion-item" key={guide?.id}>
            <div className="accordion-header">
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#guide-${guide?.id}`}
                aria-expanded="false"
                aria-controls={`guide-${guide?.id}`}
              >
                <div className="accordion-title-wrapper">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: highlightText(guide?.title || ""),
                    }}
                  ></div>
                  <button
                    className="guide-share-btn"
                    type="button"
                    title="Copy share link"
                    onClick={(e) => handleShareGuide(e, guide?.id)}
                  >
                    <ShareIcon />
                  </button>
                </div>
              </button>
            </div>
            <div
              id={`guide-${guide?.id}`}
              className="accordion-collapse collapse"
            >
              <div className="accordion-body">
                {guide?.description && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: highlightText(guide.description),
                    }}
                  />
                )}
                {hasChildren && (
                  <RecursiveAccordion
                    guides={guide.childUserGuides}
                    highlightText={highlightText}
                    depth={depth + 1}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const stripHtml = (html: string): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const filterGuidesRecursive = (
  guides: any[],
  lowerSearch: string
): any[] => {
  return guides
    .map((guide: any) => {
      const titleMatch = guide?.title?.toLowerCase()?.includes(lowerSearch);

      // Strip HTML tags before matching — avoids matching inside attribute values or tag names
      const plainDescription = guide?.description
        ? stripHtml(guide.description)
        : "";
      const descMatch = plainDescription.toLowerCase()?.includes(lowerSearch);

      const filteredChildren = guide?.childUserGuides
        ? filterGuidesRecursive(guide.childUserGuides, lowerSearch)
        : [];

      if (titleMatch || descMatch || filteredChildren.length > 0) {
        return { ...guide, childUserGuides: filteredChildren };
      }
      return null;
    })
    .filter(Boolean);
};

const Faq = () => {
  const userGuidesData = useAppSelector(userGuides);
  const [searchVal, setSearchVal] = useState<string>("");
  const [faqData, setFaqData] = useState<any[]>([]);

  const { getUserGuidesAPI, getUserGuidesLoading } = useFaqs();

  const escapeRegExp = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightText = (html: string) => {
    if (!searchVal) return html;

    const escapedSearchVal = escapeRegExp(searchVal);
    const regex = new RegExp(`(${escapedSearchVal})`, "gi");

    // Parse into DOM, walk only text nodes, highlight within them
    const div = document.createElement("div");
    div.innerHTML = html;

    const walkAndHighlight = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        if (regex.test(text)) {
          regex.lastIndex = 0; // reset after .test()
          const span = document.createElement("span");
          span.innerHTML = text.replace(regex, `<mark>$1</mark>`);
          node.parentNode?.replaceChild(span, node);
        }
      } else {
        // Don't highlight inside <script>, <style>, or HTML attributes
        const tag = (node as Element).tagName?.toLowerCase();
        if (tag !== "script" && tag !== "style") {
          Array.from(node.childNodes).forEach(walkAndHighlight);
        }
      }
    };

    Array.from(div.childNodes).forEach(walkAndHighlight);
    return div.innerHTML;
  };

  useEffect(() => {
    const sections = userGuidesData?.childUserGuides || [];

    if (!searchVal) {
      setFaqData(sections);
    } else {
      const lowerSearch = searchVal.toLowerCase();
      const filteredData = filterGuidesRecursive(sections, lowerSearch);
      setFaqData(filteredData);
    }
  }, [searchVal, userGuidesData]);

  useEffect(() => {
    getUserGuidesAPI().catch((err) =>
      console.error("Failed to load User Guides", err)
    );
  }, []);

  return (
    <div className="faqs-wrapper">
      <CommonHeader
        pageTitle={userGuidesData?.title || "User Guide"}
        pageSubtitle={userGuidesData?.description || "Search here to get answers to your questions"}
        searchValue={searchVal}
        handleSearchChange={setSearchVal}
      />

      {getUserGuidesLoading ? (
        <Loading />
      ) : (
        <div className="faq-list-wrapper">
          {faqData?.length > 0 ? (
            faqData.map((section: any) => (
              <div className="faq-section" key={section?.id}>
                <h4 className="faq-section-title">{section?.title}</h4>
                <RecursiveAccordion
                  guides={section?.childUserGuides || []}
                  highlightText={highlightText}
                />
              </div>
            ))
          ) : (
            <p className="no-results">
              No Documents found {searchVal ? `for "${searchVal}"` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Faq;